import { Search, Layers, Map, MapPin } from "lucide-react";
import type { TabType } from "../App";

interface BottomNavProps {
  isHidden: boolean;
  activeTab: TabType;
  setActiveTab: (tab: TabType) => void;
  hasLocationFilters: boolean;
  hasCategoryFilters: boolean;
  hasSearchFilters: boolean;
}

export function BottomNav({ isHidden, activeTab, setActiveTab, hasLocationFilters, hasCategoryFilters, hasSearchFilters }: BottomNavProps) {
  const hasActiveTab = activeTab !== "maps";

  const handleLocationClick = () => {
    if (activeTab === "location") {
      setActiveTab("maps");
    } else {
      setActiveTab("location");
    }
  };

  const handleCategoriesClick = () => {
    if (activeTab === "categories") {
      setActiveTab("maps");
    } else {
      setActiveTab("categories");
    }
  };

  const handleSearchClick = () => {
    if (activeTab === "search") {
      setActiveTab("maps");
    } else {
      setActiveTab("search");
    }
  };

  const handleMapsClick = () => {
    setActiveTab("maps");
  };

  const handleAboutClick = () => {
    if (activeTab === "about") {
      setActiveTab("maps");
    } else {
      setActiveTab("about");
    }
  };

  const getLocationAriaLabel = () => {
    return activeTab === "location" ? "Close location" : "Location";
  };

  const getCategoriesAriaLabel = () => {
    if (activeTab === "categories") {
      const hasFilters = hasCategoryFilters || hasLocationFilters || hasSearchFilters;
      return hasFilters ? "Apply filters and close categories" : "Close categories";
    }
    return "Categories";
  };

  const getSearchAriaLabel = () => {
    return activeTab === "search" ? "Close search" : "Search";
  };

  const getAboutAriaLabel = () => {
    return activeTab === "about" ? "Close about" : "About";
  };

  return (
    <>
      {/* About button - fixed in bottom left corner */}
      <button
        onClick={handleAboutClick}
        className={`${
          hasActiveTab && activeTab !== "about"
            ? `opacity-0 pointer-events-none invisible`
            : ``
        } group fixed bottom-[3px] md:bottom-0 left-[-4px] md:left-[-4px] z-[60] w-[60px] h-[60px] rounded-full flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent ${
          isHidden ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}
        style={{ background: 'rgba(0, 7, 28, 0)' }}
        aria-label={getAboutAriaLabel()}
        aria-pressed={activeTab === "about"}
        aria-hidden={(hasActiveTab && activeTab !== "about") || isHidden}
        tabIndex={(hasActiveTab && activeTab !== "about") || isHidden ? -1 : 0}
      >
        <div className={`w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center shadow-[0_4px_15px_rgba(0,0,0,0.4)] backdrop-blur-sm pointer-events-none ${
          activeTab === "about"
            ? "bg-gradient-to-r from-[var(--gradient-nav-active-start)] to-[var(--gradient-nav-active-end)] shadow-[0_0_25px_rgba(var(--accent-blue-rgb),0.5)] group-hover:shadow-[0_0_25px_rgba(var(--accent-blue-rgb),0.5)] border border-[var(--accent-blue)] opacity-100"
            : "bg-black/40 border border-white/10 opacity-60 group-hover:bg-gradient-to-r group-hover:from-[var(--gradient-nav-active-start)] group-hover:to-[var(--gradient-nav-active-end)] group-hover:shadow-[0_0_25px_rgba(var(--accent-blue-rgb),0.5)] group-hover:border-[var(--accent-blue)] group-hover:opacity-100"
        }`}>
          <span className="text-white text-lg md:text-xl">?</span>
        </div>
      </button>

      <nav 
        id="bottom-navigation"
        tabIndex={-1}
        className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-full max-w-[1700px] px-4 transition-opacity duration-0 ${
          isHidden ? 'opacity-0 pointer-events-none' : ''
        }`}
        aria-hidden={isHidden}
      >
        <div className="relative flex items-center justify-center">
          {/* Transparent container for all 4 buttons - vertically centered */}
          <div className="flex items-center gap-1">
            {/* Maps button */}
            <button
              onClick={handleMapsClick}
              className="group w-9 h-9 md:w-10 md:h-10 flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
              aria-label="Maps"
              aria-pressed={activeTab === "maps"}
            >
              <Map 
                className={`w-6 h-6 md:w-7 md:h-7 relative z-10 transition-colors ${
                  activeTab === "maps" ? "text-[var(--accent-blue)]" : "text-white group-hover:text-[var(--accent-blue)]"
                }`}
                strokeWidth={2}
              />
            </button>

            {/* Divider */}
            <div className="h-6 border-l border-white/20"></div>

            {/* Location button */}
            <button
              onClick={handleLocationClick}
              className={`group w-9 h-9 md:w-10 md:h-10 flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent relative`}
              aria-label={getLocationAriaLabel()}
              aria-pressed={activeTab === "location"}
            >
              <MapPin 
                className={`w-6 h-6 md:w-7 md:h-7 relative z-10 transition-colors ${
                  activeTab === "location" ? "text-[var(--accent-blue)]" : "text-white group-hover:text-[var(--accent-blue)]"
                }`}
                strokeWidth={2}
              />
            </button>

            {/* Divider */}
            <div className="h-6 border-l border-white/20"></div>

            {/* Categories button */}
            <button
              onClick={handleCategoriesClick}
              className={`group w-9 h-9 md:w-10 md:h-10 flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent relative`}
              aria-label={getCategoriesAriaLabel()}
              aria-pressed={activeTab === "categories"}
            >
              <Layers 
                className={`w-6 h-6 md:w-7 md:h-7 relative z-10 transition-colors ${
                  activeTab === "categories" ? "text-[var(--accent-blue)]" : "text-white group-hover:text-[var(--accent-blue)]"
                }`}
                strokeWidth={2}
              />
            </button>

            {/* Divider */}
            <div className="h-6 border-l border-white/20"></div>

            {/* Search button */}
            <button
              onClick={handleSearchClick}
              className={`group w-9 h-9 md:w-10 md:h-10 flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent relative`}
              aria-label={getSearchAriaLabel()}
              aria-pressed={activeTab === "search"}
            >
              <Search 
                className={`w-6 h-6 md:w-7 md:h-7 relative z-10 transition-colors ${
                  activeTab === "search" ? "text-[var(--accent-blue)]" : "text-white group-hover:text-[var(--accent-blue)]"
                }`}
                strokeWidth={2}
              />
            </button>
          </div>
        </div>
      </nav>
    </>
  );
}